import React, { useState } from 'react';
import { 
  Cloud, 
  Check, 
  Copy, 
  X, 
  Terminal, 
  Database, 
  Cpu, 
  Globe, 
  ShieldCheck, 
  ExternalLink,
  Sparkles
} from 'lucide-react';

interface CloudflareDeployModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CloudflareDeployModal: React.FC<CloudflareDeployModalProps> = ({ isOpen, onClose }) => {
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);

  if (!isOpen) return null;

  const copyCommand = (cmd: string, key: string) => {
    navigator.clipboard.writeText(cmd);
    setCopiedCmd(key);
    setTimeout(() => setCopiedCmd(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto animate-in fade-in">
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl my-8">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-orange-500/10 text-orange-400 border border-orange-500/20">
              <Cloud className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold text-white">Deploy to Cloudflare Free Tier</h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  100% Free Forever
                </span>
              </div>
              <p className="text-xs text-slate-400">Zero hosting, database, or AI fees (£0/month)</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Free Tier Architecture Highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80 space-y-1">
            <div className="flex items-center space-x-1.5 text-xs font-bold text-orange-400">
              <Globe className="w-4 h-4" />
              <span>Cloudflare Pages</span>
            </div>
            <p className="text-[11px] text-slate-400">Unlimited bandwidth, custom domains, free automated SSL.</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80 space-y-1">
            <div className="flex items-center space-x-1.5 text-xs font-bold text-sky-400">
              <Cpu className="w-4 h-4" />
              <span>Workers AI</span>
            </div>
            <p className="text-[11px] text-slate-400">Free daily Llama 3.1 GPU inference for CV tailoring & scoring.</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80 space-y-1">
            <div className="flex items-center space-x-1.5 text-xs font-bold text-emerald-400">
              <Database className="w-4 h-4" />
              <span>D1 SQL Database</span>
            </div>
            <p className="text-[11px] text-slate-400">5M reads/day & 5GB storage free for applications and leads.</p>
          </div>
        </div>

        {/* Step-by-Step Deployment Instructions */}
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-white flex items-center space-x-2">
            <Terminal className="w-4 h-4 text-sky-400" />
            <span>2-Minute Deployment Steps</span>
          </h4>

          {/* Step 1 */}
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-200">Step 1: Build the production bundle</span>
              <button
                onClick={() => copyCommand('npm run build', 'step1')}
                className="flex items-center space-x-1 text-[11px] text-slate-400 hover:text-sky-400"
              >
                {copiedCmd === 'step1' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedCmd === 'step1' ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <pre className="p-2.5 rounded-xl bg-slate-900 text-xs text-sky-300 font-mono overflow-x-auto">
              npm run build
            </pre>
          </div>

          {/* Step 2 */}
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-200">Step 2: Create your free Cloudflare D1 Database</span>
              <button
                onClick={() => copyCommand('npx wrangler d1 create jobhunt-db\nnpx wrangler d1 execute jobhunt-db --file=./schema.sql', 'step2')}
                className="flex items-center space-x-1 text-[11px] text-slate-400 hover:text-sky-400"
              >
                {copiedCmd === 'step2' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedCmd === 'step2' ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <pre className="p-2.5 rounded-xl bg-slate-900 text-xs text-sky-300 font-mono overflow-x-auto">
              npx wrangler d1 create jobhunt-db{"\n"}
              npx wrangler d1 execute jobhunt-db --file=./schema.sql
            </pre>
          </div>

          {/* Step 3 */}
          <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-200">Step 3: Deploy to Cloudflare Pages</span>
              <button
                onClick={() => copyCommand('npx wrangler pages deploy dist', 'step3')}
                className="flex items-center space-x-1 text-[11px] text-slate-400 hover:text-sky-400"
              >
                {copiedCmd === 'step3' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedCmd === 'step3' ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <pre className="p-2.5 rounded-xl bg-slate-900 text-xs text-sky-300 font-mono overflow-x-auto">
              npx wrangler pages deploy dist
            </pre>
            <p className="text-[11px] text-slate-400 pt-1">
              Wrangler will give you a live HTTPS URL (e.g. <code>https://job-hunt-revenue-engine.pages.dev</code>) in under 30 seconds!
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-800">
          <a
            href="https://dash.cloudflare.com/"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-orange-400 hover:underline flex items-center space-x-1"
          >
            <span>Open Cloudflare Dashboard</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>

          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-white"
          >
            Got It
          </button>
        </div>

      </div>
    </div>
  );
};
